import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from 'design-system';
import {
  TableHead,
  TableRow,
  Table,
  TableCell,
} from 'react-scale/components/design-system/product';
import { INamedObjectTypes } from '@scale/shared/types/lookup';
import SmartLink from 'react-scale/corp/components/SmartLink/SmartLink';
import Typography from '@mui/material/Typography';
import _ from 'lodash';

interface WarningTasksDialogProps {
  claimedTasksInfo: Record<string, string>[];
  setClaimedTasksInfo: (value: Record<string, string>[]) => void;
  notInProjectTasks: Record<string, string>[];
  setNotInProjectTasks: (value: Record<string, string>[]) => void;
  handleCreateAuditBatch: (idsToRemove?: string[]) => Promise<void>;
}

const WarningTasksDialog: React.FC<WarningTasksDialogProps> = ({
  claimedTasksInfo,
  setClaimedTasksInfo,
  notInProjectTasks,
  setNotInProjectTasks,
  handleCreateAuditBatch,
}) => {
  return (
    <Dialog
      open={!_.isEmpty(claimedTasksInfo) || !_.isEmpty(notInProjectTasks)}
      onClose={() => {
        setClaimedTasksInfo([]);
        setNotInProjectTasks([]);
      }}
    >
      <DialogTitle>
        Include tasks already claimed or from another project in the speed audit?
      </DialogTitle>
      <DialogContent>
        {!_.isEmpty(notInProjectTasks) && (
          <div style={{ marginBottom: !_.isEmpty(claimedTasksInfo) ? '32px' : '0px' }}>
            <div>
              <Typography variant="h5">
                Tasks/Subtasks not assigned to the selected project:
              </Typography>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell align="left">ObjectId</TableCell>
                    <TableCell align="left">Assigned Project</TableCell>
                  </TableRow>
                </TableHead>
                {notInProjectTasks.map(info => (
                  <TableRow key={info.id}>
                    <TableCell>{info.id}</TableCell>
                    <TableCell>
                      {' '}
                      <SmartLink type={INamedObjectTypes.Project} id={info.project} />
                    </TableCell>
                  </TableRow>
                ))}
              </Table>
            </div>
            <div style={{ marginTop: '8px' }}>
              <Typography variant="h6">Warning:</Typography>
              Speed Audits can only be created with tasks from the same project.
            </div>
          </div>
        )}
        {!_.isEmpty(claimedTasksInfo) && (
          <div>
            <div>
              <Typography variant="h5">Currently Claimed Tasks/Subtasks:</Typography>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell align="left">ObjectId</TableCell>
                    <TableCell align="left">Claimed By</TableCell>
                    <TableCell align="left">Claimed Until</TableCell>
                  </TableRow>
                </TableHead>
                {claimedTasksInfo.map(info => (
                  <TableRow key={info.id}>
                    <TableCell>{info.id}</TableCell>
                    <TableCell>{info.claimedBy}</TableCell>
                    <TableCell>{info.claimedUntil}</TableCell>
                  </TableRow>
                ))}
              </Table>
            </div>
            <div style={{ marginTop: '8px' }}>
              <Typography variant="h6">Warning:</Typography>
              If you include claimed tasks, you might overwrite someone else's work, and you may
              also override their claim. Please be sure if you select this option!
            </div>
          </div>
        )}
      </DialogContent>
      <DialogActions style={{ padding: '16px 24px' }}>
        <Button
          type="tertiary"
          onClick={() => {
            setClaimedTasksInfo([]);
            setNotInProjectTasks([]);
          }}
        >
          Cancel Audit Creation
        </Button>
        <div style={{ display: 'flex', flex: '1 0 0', justifyContent: 'flex-end' }}>
          {_.isEmpty(notInProjectTasks) && (
            <Button
              type="secondary"
              variant="outlined"
              onClick={async () => {
                await handleCreateAuditBatch();
              }}
            >
              Include claimed tasks
            </Button>
          )}
          <Button
            type="primary"
            onClick={async () => {
              const claimedTaskIds = claimedTasksInfo.map(info => info.id);
              const notInProjectTasksIds = notInProjectTasks.map(info => info.id);
              await handleCreateAuditBatch([...claimedTaskIds, ...notInProjectTasksIds]);
            }}
          >
            Don't include errored tasks
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default WarningTasksDialog;
